import React, { useState, useEffect, useRef } from 'react';
import { Bell, Clock, Calendar, CheckCircle, AlertTriangle, AlertCircle, Upload } from 'lucide-react';
import axios from 'axios';

const ICON_MAP = {
  'clock': Clock,
  'calendar': Calendar,
  'check-circle': CheckCircle,
  'alert-triangle': AlertTriangle,
  'alert-circle': AlertCircle,
  'upload': Upload,
};

const PRIORITY_STYLES = {
  high: 'border-l-amber-500 bg-amber-50/50',
  medium: 'border-l-blue-400 bg-blue-50/30',
  low: 'border-l-emerald-400 bg-emerald-50/30',
};

const ICON_COLORS = {
  upcoming: 'text-blue-500',
  published: 'text-emerald-500',
  failed: 'text-red-500',
  overdue: 'text-amber-500',
  portal_upload: 'text-violet-500',
};

export function NotificationBell() {
  const [notifications, setNotifications] = useState([]);
  const [open, setOpen] = useState(false);
  const [seen, setSeen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 60000);
    return () => clearInterval(interval);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Refetch when client changes
  useEffect(() => {
    const handleStorage = (e) => {
      if (e.key === 'activeClientSlug') fetchNotifications();
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const fetchNotifications = async () => {
    try {
      const { data } = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/notifications`);
      setNotifications(data.notifications || []);
      setSeen(false);
    } catch {
      // silent
    }
  };

  const highCount = notifications.filter(n => n.priority === 'high').length;
  const totalCount = notifications.length;

  const handleOpen = () => {
    setOpen(!open);
    setSeen(true);
  };

  return (
    <div className="relative" ref={ref} data-testid="notification-bell">
      <button
        onClick={handleOpen}
        className="relative p-2 rounded-lg hover:bg-gray-100 transition-colors"
        data-testid="notification-bell-btn"
        aria-label="Notifications"
      >
        <Bell className="w-5 h-5 text-gray-500" />
        {totalCount > 0 && !seen && (
          <span
            className={`absolute -top-0.5 -right-0.5 flex items-center justify-center min-w-[18px] h-[18px] rounded-full text-[10px] font-bold text-white px-1 ${
              highCount > 0 ? 'bg-red-500' : 'bg-blue-500'
            }`}
            data-testid="notification-badge"
          >
            {totalCount}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 top-full mt-2 w-[360px] max-h-[480px] overflow-y-auto bg-white border border-gray-200 rounded-xl shadow-2xl z-[100]"
          data-testid="notification-dropdown"
        >
          <div className="px-4 py-3 border-b border-gray-100">
            <h3 className="text-sm font-semibold text-gray-800">Notifications</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              {totalCount === 0
                ? 'All caught up!'
                : `${totalCount} update${totalCount > 1 ? 's' : ''}`}
            </p>
          </div>

          {notifications.length === 0 ? (
            <div className="px-4 py-8 text-center text-gray-400 text-sm">
              No notifications right now
            </div>
          ) : (
            <div className="py-1">
              {notifications.map((n, i) => {
                const IconComp = ICON_MAP[n.icon] || Bell;
                const iconColor = ICON_COLORS[n.type] || 'text-gray-400';
                const bgStyle = PRIORITY_STYLES[n.priority] || '';
                return (
                  <div
                    key={n.id + '-' + i}
                    className={`px-4 py-3 border-l-[3px] ${bgStyle} hover:bg-gray-50/80 transition-colors`}
                    data-testid={`notification-item-${i}`}
                  >
                    <div className="flex items-start gap-2.5">
                      <IconComp className={`w-4 h-4 mt-0.5 shrink-0 ${iconColor}`} />
                      <p className="text-[13px] text-gray-700 leading-snug">
                        {n.message}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
